import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "modalContent",
    "step1", "step2",
    "stepIndicator",
    "templateGrid",
    "blockList",
    "preview",
    "channelInput",
    "blocksInput",
    "templateTypeInput",
    "templateNameInput",
    "addBlockDropdown",
    "nextButton",
    "saveButton",
    "backButton"
  ]

  static values = {
    templates: Object,
    blockTypes: Object,
    currentStep: { type: Number, default: 1 },
    selectedTemplate: String,
    channel: String
  }

  connect() {
    console.log('[SlackMessageBuilder] Controller connected')
    console.log('[SlackMessageBuilder] Templates available:', Object.keys(this.templatesValue || {}))
    console.log('[SlackMessageBuilder] Block types available:', Object.keys(this.blockTypesValue || {}))
    
    this.blocks = []
    this.loadInitialData()
    
    console.log('[SlackMessageBuilder] After loadInitialData, blocks:', this.blocks)
  }

  loadInitialData() {
    // Load existing blocks from hidden input if editing
    if (this.hasBlocksInputTarget && this.blocksInputTarget.value) {
      try {
        this.blocks = JSON.parse(this.blocksInputTarget.value)
      } catch (e) {
        this.blocks = []
      }
    }

    // Load channel
    if (this.hasChannelInputTarget && this.channelInputTarget.value) {
      this.channelValue = this.channelInputTarget.value
    }

    // Load template info
    if (this.hasTemplateNameInputTarget && this.templateNameInputTarget.value) {
      this.selectedTemplateValue = this.templateNameInputTarget.value
    }
  }

  // Modal Management
  open() {
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    
    // If blocks already exist, go directly to Configure (step 2)
    // Otherwise start at Template selection (step 1)
    if (this.blocks && this.blocks.length > 0) {
      this.goToStep(2)
    } else {
      this.goToStep(1)
    }
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  // Clicking outside modal closes it
  backdropClick(event) {
    if (event.target === this.modalTarget) {
      this.close()
    }
  }

  // Prevent clicks inside modal from closing it
  stopPropagation(event) {
    event.stopPropagation()
  }

  // Step Navigation
  goToStep(step) {
    console.log('[SlackMessageBuilder] goToStep:', step, 'current blocks:', this.blocks?.length)
    this.currentStepValue = step
    this.updateStepVisibility()
    this.updateStepIndicators()
  }

  nextStep() {
    console.log('[SlackMessageBuilder] nextStep called, currentStep:', this.currentStepValue, 'blocks:', this.blocks?.length)
    if (this.currentStepValue < 2) {
      this.goToStep(this.currentStepValue + 1)
    }
  }

  previousStep() {
    console.log('[SlackMessageBuilder] previousStep called')
    if (this.currentStepValue > 1) {
      this.goToStep(this.currentStepValue - 1)
    }
  }

  updateStepVisibility() {
    console.log('[SlackMessageBuilder] updateStepVisibility, step:', this.currentStepValue)
    console.log('[SlackMessageBuilder] hasStep1Target:', this.hasStep1Target, 'hasStep2Target:', this.hasStep2Target)
    
    // Only 2 steps now: Template (step1) and Configure (step2)
    if (this.hasStep1Target) {
      this.step1Target.classList.toggle("hidden", this.currentStepValue !== 1)
    }
    if (this.hasStep2Target) {
      this.step2Target.classList.toggle("hidden", this.currentStepValue !== 2)
    }

    // Update preview when entering step 2
    if (this.currentStepValue === 2) {
      console.log('[SlackMessageBuilder] Entering step 2, blocks to render:', this.blocks)
      console.log('[SlackMessageBuilder] hasBlockListTarget:', this.hasBlockListTarget)
      console.log('[SlackMessageBuilder] hasPreviewTarget:', this.hasPreviewTarget)
      this.updatePreview()
      this.renderBlockList()
    }

    // Update button visibility
    this.updateButtonVisibility()
  }

  updateButtonVisibility() {
    // Show Next on step 1, Save on step 2
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", this.currentStepValue === 2)
    }
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.toggle("hidden", this.currentStepValue !== 2)
    }
  }

  updateStepIndicators() {
    if (!this.hasStepIndicatorTarget) return

    const indicators = this.stepIndicatorTarget.querySelectorAll("[data-step]")
    indicators.forEach(indicator => {
      const step = parseInt(indicator.dataset.step)
      const isActive = step === this.currentStepValue
      const circle = indicator.querySelector("span")
      
      // Update text and border styling
      indicator.classList.toggle("text-[#4A154B]", isActive)
      indicator.classList.toggle("border-[#4A154B]", isActive)
      indicator.classList.toggle("text-gray-500", !isActive)
      indicator.classList.toggle("border-transparent", !isActive)
      
      // Update circle styling
      if (circle) {
        circle.classList.toggle("bg-[#4A154B]", isActive)
        circle.classList.toggle("text-white", isActive)
        circle.classList.toggle("bg-gray-300", !isActive)
        circle.classList.toggle("text-gray-600", !isActive)
      }
    })
  }

  goToStepFromIndicator(event) {
    const step = parseInt(event.currentTarget.dataset.step)
    if (step) {
      this.goToStep(step)
    }
  }

  // Template Selection (Step 1)
  selectTemplate(event) {
    const templateName = event.currentTarget.dataset.template
    console.log('[SlackMessageBuilder] selectTemplate called with:', templateName)
    
    this.selectedTemplateValue = templateName

    // Highlight selected template
    this.templateGridTarget.querySelectorAll("[data-template]").forEach(el => {
      el.classList.toggle("ring-2", el.dataset.template === templateName)
      el.classList.toggle("ring-[#4A154B]", el.dataset.template === templateName)
      el.classList.toggle("border-[#4A154B]", el.dataset.template === templateName)
    })

    // Load template blocks
    const template = this.templatesValue[templateName]
    console.log('[SlackMessageBuilder] Template data:', template)
    
    if (template) {
      this.blocks = JSON.parse(JSON.stringify(template.blocks || []))
      console.log('[SlackMessageBuilder] Loaded blocks:', this.blocks)
    } else {
      console.warn('[SlackMessageBuilder] Template not found:', templateName)
      this.blocks = []
    }
  }

  // Block Management (Step 2)
  addBlock(event) {
    const blockType = event.currentTarget.dataset.blockType
    const newBlock = this.createNewBlock(blockType)
    if (newBlock) {
      this.blocks.push(newBlock)
      this.renderBlockList()
      this.updatePreview()
    }
    // Close dropdown
    this.hideAddBlockDropdown()
  }

  createNewBlock(type) {
    switch (type) {
      case "header":
        return {
          type: "header",
          text: { type: "plain_text", text: "Header text" }
        }
      case "section":
        return {
          type: "section",
          text: { type: "mrkdwn", text: "Section text with *markdown* support" }
        }
      case "divider":
        return { type: "divider" }
      case "context":
        return {
          type: "context",
          elements: [{ type: "mrkdwn", text: "Context text" }]
        }
      case "image":
        return {
          type: "image",
          image_url: "https://example.com/image.png",
          alt_text: "Image description"
        }
      default:
        return null
    }
  }

  removeBlock(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.blocks.splice(index, 1)
    this.renderBlockList()
    this.updatePreview()
  }

  moveBlockUp(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (index > 0) {
      [this.blocks[index - 1], this.blocks[index]] = [this.blocks[index], this.blocks[index - 1]]
      this.renderBlockList()
      this.updatePreview()
    }
  }

  moveBlockDown(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (index < this.blocks.length - 1) {
      [this.blocks[index], this.blocks[index + 1]] = [this.blocks[index + 1], this.blocks[index]]
      this.renderBlockList()
      this.updatePreview()
    }
  }

  updateBlockText(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    const value = event.currentTarget.value
    const block = this.blocks[index]

    if (block.type === "header") {
      block.text.text = value
    } else if (block.type === "section") {
      block.text.text = value
    } else if (block.type === "context") {
      block.elements[0].text = value
    } else if (block.type === "image") {
      if (event.currentTarget.dataset.field === "url") {
        block.image_url = value
      } else if (event.currentTarget.dataset.field === "alt") {
        block.alt_text = value
      }
    }

    this.updatePreview()
  }

  updateBlockAccessory(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    const accessoryType = event.currentTarget.value
    const block = this.blocks[index]

    if (accessoryType === "none") {
      delete block.accessory
    } else if (accessoryType === "button") {
      block.accessory = {
        type: "button",
        text: { type: "plain_text", text: "Button" },
        url: "https://example.com"
      }
    } else if (accessoryType === "image") {
      block.accessory = {
        type: "image",
        image_url: "https://example.com/image.png",
        alt_text: "Image"
      }
    }

    this.renderBlockList()
    this.updatePreview()
  }

  updateAccessoryField(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    const field = event.currentTarget.dataset.field
    const value = event.currentTarget.value
    const block = this.blocks[index]

    if (!block.accessory) return

    if (field === "text") {
      block.accessory.text.text = value
    } else if (field === "url") {
      block.accessory.url = value
    } else if (field === "imageUrl") {
      block.accessory.image_url = value
    } else if (field === "altText") {
      block.accessory.alt_text = value
    }

    this.updatePreview()
  }

  // Toggle add block dropdown
  toggleAddBlockDropdown() {
    this.addBlockDropdownTarget.classList.toggle("hidden")
  }

  hideAddBlockDropdown() {
    this.addBlockDropdownTarget.classList.add("hidden")
  }

  // Render block list in step 2
  renderBlockList() {
    console.log('[SlackMessageBuilder] renderBlockList called')
    console.log('[SlackMessageBuilder] this.blocks:', this.blocks)
    console.log('[SlackMessageBuilder] blocks length:', this.blocks?.length)
    
    if (!this.hasBlockListTarget) {
      console.error('[SlackMessageBuilder] blockListTarget not found!')
      return
    }
    
    let html = ""

    if (this.blocks && this.blocks.length > 0) {
      console.log('[SlackMessageBuilder] Rendering', this.blocks.length, 'blocks')
      this.blocks.forEach((block, index) => {
        console.log('[SlackMessageBuilder] Rendering block', index, ':', block.type)
        html += this.renderBlockEditor(block, index)
      })
    } else {
      console.log('[SlackMessageBuilder] No blocks, showing empty state')
      html = `
        <div class="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <p>No blocks yet. Click "Add Block" to start building your message.</p>
        </div>
      `
    }

    this.blockListTarget.innerHTML = html
    console.log('[SlackMessageBuilder] blockListTarget updated')
  }

  renderBlockEditor(block, index) {
    const isFirst = index === 0
    const isLast = index === this.blocks.length - 1

    let content = ""

    switch (block.type) {
      case "header":
        content = `
          <label class="block text-xs text-gray-500 mb-1">Header Text</label>
          <input type="text" 
            value="${this.escapeHtml(block.text?.text || "")}"
            data-index="${index}"
            data-action="input->slack-message-builder#updateBlockText"
            class="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-black"
            placeholder="Header text...">
        `
        break

      case "section":
        content = `
          <label class="block text-xs text-gray-500 mb-1">Section Text (supports *markdown*)</label>
          <textarea 
            data-index="${index}"
            data-action="input->slack-message-builder#updateBlockText"
            class="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-black"
            rows="2"
            placeholder="Section text...">${this.escapeHtml(block.text?.text || "")}</textarea>
          
          <div class="mt-3">
            <label class="block text-xs text-gray-500 mb-1">Accessory</label>
            <select 
              data-index="${index}"
              data-action="change->slack-message-builder#updateBlockAccessory"
              class="px-2 py-1 border border-gray-200 text-sm bg-white">
              <option value="none" ${!block.accessory ? "selected" : ""}>None</option>
              <option value="button" ${block.accessory?.type === "button" ? "selected" : ""}>Button</option>
              <option value="image" ${block.accessory?.type === "image" ? "selected" : ""}>Image</option>
            </select>
          </div>
          ${this.renderAccessoryFields(block, index)}
        `
        break

      case "divider":
        content = `
          <div class="border-t border-gray-300 my-2"></div>
          <p class="text-xs text-gray-400 text-center">Horizontal divider</p>
        `
        break

      case "context":
        content = `
          <label class="block text-xs text-gray-500 mb-1">Context Text (small, muted)</label>
          <input type="text" 
            value="${this.escapeHtml(block.elements?.[0]?.text || "")}"
            data-index="${index}"
            data-action="input->slack-message-builder#updateBlockText"
            class="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-black"
            placeholder="Context text...">
        `
        break

      case "image":
        content = `
          <label class="block text-xs text-gray-500 mb-1">Image URL</label>
          <input type="text" 
            value="${this.escapeHtml(block.image_url || "")}"
            data-index="${index}"
            data-field="url"
            data-action="input->slack-message-builder#updateBlockText"
            class="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-black mb-2"
            placeholder="https://example.com/image.png">
          <label class="block text-xs text-gray-500 mb-1">Alt Text</label>
          <input type="text" 
            value="${this.escapeHtml(block.alt_text || "")}"
            data-index="${index}"
            data-field="alt"
            data-action="input->slack-message-builder#updateBlockText"
            class="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-black"
            placeholder="Image description">
        `
        break
    }

    return `
      <div class="border border-gray-200 p-4 bg-white" data-block-index="${index}">
        <div class="flex items-center justify-between mb-3">
          <span class="text-xs font-medium text-gray-700 uppercase tracking-wide">${block.type}</span>
          <div class="flex items-center gap-1">
            <button type="button" 
              data-index="${index}" 
              data-action="click->slack-message-builder#moveBlockUp"
              class="p-1 text-gray-400 hover:text-gray-600 ${isFirst ? "opacity-30 cursor-not-allowed" : ""}"
              ${isFirst ? "disabled" : ""}>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
              </svg>
            </button>
            <button type="button" 
              data-index="${index}" 
              data-action="click->slack-message-builder#moveBlockDown"
              class="p-1 text-gray-400 hover:text-gray-600 ${isLast ? "opacity-30 cursor-not-allowed" : ""}"
              ${isLast ? "disabled" : ""}>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
            <button type="button" 
              data-index="${index}" 
              data-action="click->slack-message-builder#removeBlock"
              class="p-1 text-red-400 hover:text-red-600 ml-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        ${content}
      </div>
    `
  }

  renderAccessoryFields(block, index) {
    if (!block.accessory) return ""

    if (block.accessory.type === "button") {
      return `
        <div class="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
          <div>
            <label class="block text-xs text-gray-500 mb-1">Button Text</label>
            <input type="text" 
              value="${this.escapeHtml(block.accessory.text?.text || "")}"
              data-index="${index}"
              data-field="text"
              data-action="input->slack-message-builder#updateAccessoryField"
              class="w-full px-2 py-1 border border-gray-200 text-sm"
              placeholder="Button text">
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">Button URL</label>
            <input type="text" 
              value="${this.escapeHtml(block.accessory.url || "")}"
              data-index="${index}"
              data-field="url"
              data-action="input->slack-message-builder#updateAccessoryField"
              class="w-full px-2 py-1 border border-gray-200 text-sm"
              placeholder="https://example.com">
          </div>
        </div>
      `
    } else if (block.accessory.type === "image") {
      return `
        <div class="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
          <div>
            <label class="block text-xs text-gray-500 mb-1">Image URL</label>
            <input type="text" 
              value="${this.escapeHtml(block.accessory.image_url || "")}"
              data-index="${index}"
              data-field="imageUrl"
              data-action="input->slack-message-builder#updateAccessoryField"
              class="w-full px-2 py-1 border border-gray-200 text-sm"
              placeholder="https://example.com/image.png">
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">Alt Text</label>
            <input type="text" 
              value="${this.escapeHtml(block.accessory.alt_text || "")}"
              data-index="${index}"
              data-field="altText"
              data-action="input->slack-message-builder#updateAccessoryField"
              class="w-full px-2 py-1 border border-gray-200 text-sm"
              placeholder="Image description">
          </div>
        </div>
      `
    }

    return ""
  }

  // Live Preview
  updatePreview() {
    let html = '<div class="slack-preview bg-white rounded border border-gray-200 shadow-sm">'

    this.blocks.forEach(block => {
      html += this.renderBlockPreview(block)
    })

    if (this.blocks.length === 0) {
      html += '<div class="p-4 text-gray-400 text-sm italic">Preview will appear here...</div>'
    }

    html += '</div>'
    this.previewTarget.innerHTML = html
  }

  renderBlockPreview(block) {
    switch (block.type) {
      case "header":
        return `<div class="px-4 py-3 font-bold text-lg">${this.escapeHtml(block.text?.text || "")}</div>`

      case "section":
        let sectionHtml = `<div class="px-4 py-2 flex justify-between items-start gap-4">`
        sectionHtml += `<div class="flex-1">${this.renderMarkdown(block.text?.text || "")}</div>`
        
        if (block.accessory) {
          if (block.accessory.type === "button") {
            sectionHtml += `
              <a href="${this.escapeHtml(block.accessory.url || "#")}" 
                 class="inline-block px-3 py-1 bg-gray-100 text-sm rounded border border-gray-300 hover:bg-gray-200 whitespace-nowrap">
                ${this.escapeHtml(block.accessory.text?.text || "Button")}
              </a>
            `
          } else if (block.accessory.type === "image") {
            sectionHtml += `
              <img src="${this.escapeHtml(block.accessory.image_url || "")}" 
                   alt="${this.escapeHtml(block.accessory.alt_text || "")}"
                   class="w-12 h-12 rounded object-cover">
            `
          }
        }
        
        sectionHtml += `</div>`
        return sectionHtml

      case "divider":
        return `<div class="px-4 py-2"><hr class="border-gray-200"></div>`

      case "context":
        return `<div class="px-4 py-1 text-xs text-gray-500">${this.renderMarkdown(block.elements?.[0]?.text || "")}</div>`

      case "image":
        return `
          <div class="px-4 py-2">
            <img src="${this.escapeHtml(block.image_url || "")}" 
                 alt="${this.escapeHtml(block.alt_text || "")}"
                 class="max-w-full rounded">
          </div>
        `

      default:
        return ""
    }
  }

  // Simple markdown rendering for preview
  renderMarkdown(text) {
    if (!text) return ""
    
    let html = this.escapeHtml(text)
    
    // Bold: *text*
    html = html.replace(/\*([^*]+)\*/g, "<strong>$1</strong>")
    
    // Italic: _text_
    html = html.replace(/_([^_]+)_/g, "<em>$1</em>")
    
    // Code: `text`
    html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-sm">$1</code>')
    
    // Links: <url|text>
    html = html.replace(/&lt;([^|>]+)\|([^>]+)&gt;/g, '<a href="$1" class="text-blue-600 underline">$2</a>')
    
    // Newlines
    html = html.replace(/\n/g, "<br>")
    
    // Highlight variable placeholders
    html = html.replace(/\{\{([^}]+)\}\}/g, '<span class="bg-yellow-100 px-1 rounded text-yellow-800 font-mono text-xs">${$1}</span>')
    
    return html
  }

  // Channel input
  updateChannel(event) {
    this.channelValue = event.currentTarget.value
  }

  // Save configuration
  save() {
    console.log('[SlackMessageBuilder] save() called')
    console.log('[SlackMessageBuilder] Saving blocks:', this.blocks)
    
    // Update hidden inputs
    if (this.hasBlocksInputTarget) {
      this.blocksInputTarget.value = JSON.stringify(this.blocks)
      console.log('[SlackMessageBuilder] Updated blocksInput to:', this.blocksInputTarget.value)
    }
    if (this.hasTemplateTypeInputTarget) {
      this.templateTypeInputTarget.value = this.selectedTemplateValue === "custom" ? "custom" : "template"
      console.log('[SlackMessageBuilder] Updated templateType to:', this.templateTypeInputTarget.value)
    }
    if (this.hasTemplateNameInputTarget) {
      this.templateNameInputTarget.value = this.selectedTemplateValue || ""
      console.log('[SlackMessageBuilder] Updated templateName to:', this.templateNameInputTarget.value)
    }

    // Update the visible summary
    this.updateConfigSummary()
    
    // Update the Paper.js block flow preview
    this.updateBlockFlowPreview()

    this.close()
  }
  
  // Update the Paper.js block flow preview after saving
  updateBlockFlowPreview() {
    console.log('[SlackMessageBuilder] updateBlockFlowPreview called, blocks:', this.blocks?.length)
    
    // Find the block-flow-preview controller element within our scope
    const previewElement = this.element.querySelector('[data-controller="block-flow-preview"]')
    
    if (previewElement && this.blocks && this.blocks.length > 0) {
      console.log('[SlackMessageBuilder] Found block-flow-preview element, updating blocks value')
      
      // Update canvas height based on block count
      const canvasHeight = Math.max(200, this.blocks.length * 86 + 40)
      const canvas = previewElement.querySelector('canvas')
      if (canvas) {
        canvas.style.height = `${canvasHeight}px`
        canvas.height = canvasHeight
      }
      
      // Update the data attribute with new blocks - this triggers blocksValueChanged
      previewElement.dataset.blockFlowPreviewBlocksValue = JSON.stringify(this.blocks)
      previewElement.dataset.blockFlowPreviewTemplateNameValue = this.selectedTemplateValue || ''
      
      console.log('[SlackMessageBuilder] Updated block-flow-preview data attributes')
    } else if (this.blocks && this.blocks.length > 0) {
      console.log('[SlackMessageBuilder] No block-flow-preview element found, creating preview section')
      // The preview section might not exist yet - we need to show it
      this.showBlockFlowPreviewSection()
    } else {
      console.log('[SlackMessageBuilder] No blocks to preview')
    }
  }
  
  // Show the block flow preview section and update it
  showBlockFlowPreviewSection() {
    console.log('[SlackMessageBuilder] showBlockFlowPreviewSection called')
    
    const configSummary = this.element.querySelector('[data-config-summary]')
    if (!configSummary) {
      console.warn('[SlackMessageBuilder] No config-summary element found')
      return
    }
    
    if (this.blocks && this.blocks.length > 0) {
      // Update canvas height based on block count
      const canvasHeight = Math.max(200, this.blocks.length * 86 + 40)
      
      // Find or update the block-flow-preview controller
      let previewContainer = configSummary.querySelector('[data-controller="block-flow-preview"]')
      
      if (previewContainer) {
        // Update existing preview
        console.log('[SlackMessageBuilder] Updating existing block-flow-preview')
        previewContainer.dataset.blockFlowPreviewBlocksValue = JSON.stringify(this.blocks)
        previewContainer.dataset.blockFlowPreviewTemplateNameValue = this.selectedTemplateValue || ''
        
        // Update canvas height
        const canvas = previewContainer.querySelector('canvas')
        if (canvas) {
          canvas.style.height = `${canvasHeight}px`
          canvas.height = canvasHeight
        }
      } else {
        // Create new preview container inside config-summary
        console.log('[SlackMessageBuilder] Creating new block-flow-preview')
        
        // First update the header section
        const headerSection = configSummary.querySelector('.flex.items-center.justify-between')
        if (!headerSection) {
          configSummary.innerHTML = `
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-gray-700" data-block-count>${this.blocks.length} block(s)</span>
                ${this.selectedTemplateValue ? `
                  <span class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-template-label>
                    ${this.templatesValue[this.selectedTemplateValue]?.name || this.selectedTemplateValue}
                  </span>
                ` : ''}
              </div>
              <button type="button"
                      data-action="click->slack-message-builder#open"
                      class="px-4 py-2 bg-[#4A154B] text-white text-sm font-medium rounded-lg hover:bg-[#611f69] transition-colors flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
                Edit Message
              </button>
            </div>
            
            <div data-controller="block-flow-preview"
                 data-block-flow-preview-blocks-value='${JSON.stringify(this.blocks).replace(/'/g, "&#39;")}'
                 data-block-flow-preview-template-name-value="${this.selectedTemplateValue || ''}"
                 class="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
              <canvas data-block-flow-preview-target="canvas"
                      class="w-full"
                      style="height: ${canvasHeight}px"
                      width="400"
                      height="${canvasHeight}"></canvas>
            </div>
          `
        }
      }
      
      configSummary.classList.remove('hidden')
      
      // Hide the empty state
      const emptyState = this.element.querySelector('[data-empty-state]')
      if (emptyState) {
        emptyState.classList.add('hidden')
      }
    }
  }

  updateConfigSummary() {
    console.log('[SlackMessageBuilder] updateConfigSummary called, blocks:', this.blocks?.length)
    
    // Find and update the summary display outside the modal
    const summaryEl = this.element.querySelector("[data-config-summary]")
    
    // Find the empty state element using the data attribute
    const emptyStateEl = this.element.querySelector('[data-empty-state]')
    
    // Also hide the legacy format warning if we now have blocks
    const legacyWarning = this.element.querySelector('[data-legacy-warning]')
    
    if (this.blocks && this.blocks.length > 0) {
      console.log('[SlackMessageBuilder] Has blocks, showing summary')
      
      // Hide empty state
      if (emptyStateEl) {
        emptyStateEl.classList.add("hidden")
      }
      
      // Hide legacy warning since we now have blocks
      if (legacyWarning) {
        legacyWarning.classList.add("hidden")
      }
      
      // Update the block count in summary
      const blockCountEl = this.element.querySelector('[data-block-count]')
      if (blockCountEl) {
        blockCountEl.textContent = `${this.blocks.length} block(s)`
      }
      
      // Update template label if exists
      const templateLabelEl = this.element.querySelector('[data-template-label]')
      if (templateLabelEl && this.selectedTemplateValue) {
        const template = this.templatesValue[this.selectedTemplateValue]
        templateLabelEl.textContent = template?.name || this.selectedTemplateValue
        templateLabelEl.classList.remove('hidden')
      }
      
      // Show summary
      if (summaryEl) {
        summaryEl.classList.remove("hidden")
      }
    } else {
      console.log('[SlackMessageBuilder] No blocks, showing empty state')
      
      // Show empty state
      if (emptyStateEl) {
        emptyStateEl.classList.remove("hidden")
      }
      
      // Hide summary
      if (summaryEl) {
        summaryEl.classList.add("hidden")
      }
    }
  }

  // Utility
  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
